import React, { useEffect, useRef, useState } from 'react';
import './OwnershipPillars.css';

const pillars = [
  {
    icon: (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
        <circle cx="9" cy="7" r="4" />
        <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
        <path d="M16 3.13a4 4 0 0 1 0 7.75" />
      </svg>
    ),
    title: 'Your Audience, Your Data',
    description: 'Every registrant, student, buyer and follower in one list. Export it anytime — no platform lock-in on your relationships.',
    gradient: 'linear-gradient(135deg, #C9A962 0%, #D4B978 100%)',
    iconBg: 'rgba(201, 169, 98, 0.15)'
  },
  {
    icon: (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
        <polyline points="7 10 12 15 17 10" />
        <line x1="12" y1="15" x2="12" y2="3" />
      </svg>
    ),
    title: 'Your Content, Always Yours',
    description: 'Download every recording, episode, and asset. Your media lives where you want it.',
    gradient: 'linear-gradient(135deg, #EC4899 0%, #D946EF 100%)',
    iconBg: 'rgba(236, 72, 153, 0.15)'
  },
  {
    icon: (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <circle cx="18" cy="5" r="3" />
        <circle cx="6" cy="12" r="3" />
        <circle cx="18" cy="19" r="3" />
        <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
        <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
      </svg>
    ),
    title: 'Distribute on Your Terms',
    description: 'Stream to YouTube, Facebook, Twitch, and beyond. Publish podcasts via RSS. Reach the living room with Canira TV. Never locked into one channel.',
    gradient: 'linear-gradient(135deg, #8B5CF6 0%, #D4B560 100%)',
    iconBg: 'rgba(139, 92, 246, 0.15)'
  },
  {
    icon: (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <line x1="12" y1="1" x2="12" y2="23" />
        <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
      </svg>
    ),
    title: 'Keep What You Earn',
    description: 'Use Stripe, PayPal, or your own payment systems. No platform cut on your revenue.',
    gradient: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
    iconBg: 'rgba(16, 185, 129, 0.15)'
  }
];

const PillarCard = ({ pillar, index }) => {
  const cardRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setTimeout(() => setIsVisible(true), index * 150);
        }
      },
      { threshold: 0.2 }
    );

    if (cardRef.current) {
      observer.observe(cardRef.current);
    }

    return () => observer.disconnect();
  }, [index]);

  return (
    <div
      ref={cardRef}
      className={`ownership-pillar-card ${isVisible ? 'visible' : ''}`}
    >
      <div className="pillar-icon" style={{ background: pillar.iconBg }}>
        {pillar.icon}
      </div>
      <h3 className="pillar-title">{pillar.title}</h3>
      <p className="pillar-description">{pillar.description}</p>
      <div className="pillar-glow" style={{ background: pillar.gradient }} />
    </div>
  );
};

const OwnershipPillars = () => {
  const headerRef = useRef(null);
  const [headerVisible, setHeaderVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setHeaderVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (headerRef.current) {
      observer.observe(headerRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section className="ownership-pillars-section">
      <div className="ownership-bg">
        <div className="ownership-bg-gradient ownership-bg-1" />
        <div className="ownership-bg-gradient ownership-bg-2" />
      </div>

      <div className="ownership-container">
        <div ref={headerRef} className={`ownership-header ${headerVisible ? 'visible' : ''}`}>
          <span className="ownership-badge">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
            </svg>
            Your Content. Your Rules.
          </span>
          <h2 className="ownership-title">
            You Own Everything
            <span className="gradient-text"> You Create</span>
          </h2>
          <p className="ownership-subtitle">
            Use Canira to produce and grow — but your content, audience, and revenue
            are always yours. Leave anytime and take everything with you.
          </p>
        </div>

        <div className="ownership-grid">
          {pillars.map((pillar, index) => (
            <PillarCard key={index} pillar={pillar} index={index} />
          ))}
        </div>

        <div className={`ownership-trust ${headerVisible ? 'visible' : ''}`}>
          <p>No lock-in. No platform tax. No strings attached.</p>
        </div>
      </div>
    </section>
  );
};

export default OwnershipPillars;
